import { zodResolver } from "@hookform/resolvers/zod";
import { EditorContent, useEditor, useEditorState } from "@tiptap/react";
import type { JSONContent } from "@tiptap/core";
import LinkExtension from "@tiptap/extension-link";
import StarterKit from "@tiptap/starter-kit";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Bold,
  CalendarDays,
  FileText,
  Heading2,
  Heading3,
  Image,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Plus,
  RotateCcw,
  Save,
  Trash2,
  Upload,
} from "lucide-react";
import {
  type ChangeEvent,
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { type Resolver, useForm, useWatch } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import {
  activityWriteSchema,
  createUploadInputSchema,
  richTextDocumentSchema,
  safeHttpUrlSchema,
  type ActivityDocument,
  type ActivityMaterial,
  type ActivityWrite,
  type DuplicateLinkCheckInput,
  type DuplicateLinkCheckResponse,
  type RichTextDocument,
  type UploadFile,
  youtubeThumbnailUrl,
  youtubeVideoIdFromUrl,
} from "@learn-flow/contracts";

import { AppShell } from "../../components/app-shell.js";
import {
  createActivityReturnState,
  parseActivityDetailReturnDestination,
  resolveActivityReturnContext,
} from "../../lib/activity-return-context.js";
import { ApiError } from "../../lib/api.js";
import { createUuid } from "../../lib/random-uuid.js";
import { calendarQueryRoot } from "../calendar/calendar-api.js";
import { formatFullDate } from "../calendar/calendar-date.js";
import { createCollection, libraryQueryRoot } from "../library/library-api.js";
import { readStoredLibraryContext } from "../library/library-context.js";
import {
  activityDetailKey,
  activityEditorKey,
  activityEditorOptionsKey,
  checkDuplicateActivityLinks,
  createActivity,
  createActivitySession,
  fetchResourceMetadata,
  loadActivityEditor,
  loadActivityEditorOptions,
  removeUpload,
  updateActivity,
  uploadFile,
} from "./activity-api.js";
import { parseCalendarActivityCreationIntent } from "./calendar-activity-creation-intent.js";
import {
  deleteActivityDraft,
  readActivityDraft,
  type ActivityDraft,
  type ActivityDraftMaterial,
  writeActivityDraft,
} from "./activity-draft-store.js";
import { ActivityAbandonDialog } from "./activity-abandon-dialog.js";
import {
  registerActivityHistoryNavigationHandler,
  setActivityNavigationDirty,
} from "./navigation-guard.js";

type EditorFields = Omit<ActivityWrite, "materials">;
type LinkEditorMaterial = Extract<ActivityMaterial, { kind: "link" }> & {
  metadataStatus?: "idle" | "loading" | "failed";
  metadataError?: string;
};
type RichTextEditorMaterial = Extract<ActivityMaterial, { kind: "rich_text" }>;
type UploadEditorMaterial = {
  id: string;
  kind: "image" | "file";
  fileId: string | null;
  file: UploadFile | null;
  altText: string;
  decorative: boolean;
  uploadStatus: "empty" | "uploading" | "ready" | "failed";
  uploadProgress: number;
  uploadError: string;
  previewUrl: string | null;
};
type EditorMaterial = LinkEditorMaterial | RichTextEditorMaterial | UploadEditorMaterial;
type MaterialFieldErrors = Record<string, string>;
type MaterialFocusRequest = {
  generation: number;
  materialId: string | null;
  selector: string;
};
type ActivityAbandonRequest = {
  proceed: () => void;
  restoreFocusTo: HTMLElement | null;
};

type ActivityRouteState = {
  activityDetailReturnTo?: string;
  activityReturnTo?: string;
  libraryReturnTo?: string;
};
type ScheduledCreation = NonNullable<ActivityDraft["scheduledCreation"]>;

type DuplicateActivityMatch = DuplicateLinkCheckResponse["links"][number]["matches"][number];
type DuplicateCheckState =
  | { status: "idle" }
  | { status: "checking"; fingerprint: string; materialIds: string[] }
  | {
      status: "duplicate";
      fingerprint: string;
      matchesByMaterial: Record<string, DuplicateActivityMatch[]>;
    }
  | {
      status: "failed";
      fingerprint: string;
      materialIds: string[];
      message: string;
    };
type MaterialDuplicateState =
  | { status: "idle" }
  | { status: "checking" }
  | { status: "duplicate"; matches: DuplicateActivityMatch[] }
  | { status: "failed"; message: string };
type SaveOptions = { skipDuplicateCheck?: boolean };

const editorFieldsSchema = activityWriteSchema.omit({ materials: true });
const activityEditorFormId = "activity-editor-form";
const emptyRichText: RichTextDocument = {
  type: "doc",
  content: [{ type: "paragraph" }],
};

export function ActivityEditorPage({ mode }: { mode: "create" | "edit" }): React.JSX.Element {
  const { activityId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const routeState = isActivityRouteState(location.state) ? location.state : {};
  const calendarCreationIntent = useMemo(
    () =>
      mode === "create"
        ? parseCalendarActivityCreationIntent(new URLSearchParams(location.search))
        : null,
    [location.search, mode],
  );
  const libraryReturnTo = routeState.libraryReturnTo?.startsWith("/library")
    ? routeState.libraryReturnTo
    : readStoredLibraryContext();
  const detailOriginReturnTo = resolveActivityReturnContext(
    routeState,
    libraryReturnTo,
  ).destination;
  const returnTo = calendarCreationIntent?.returnTo ?? libraryReturnTo;
  const detailReturnTo =
    mode === "edit"
      ? parseActivityDetailReturnDestination(routeState.activityDetailReturnTo, activityId ?? "")
      : null;
  const draftKey =
    mode === "create"
      ? calendarCreationIntent
        ? `activity:new:calendar:${calendarCreationIntent.scheduledDate}:${calendarCreationIntent.returnTo}`
        : "activity:new"
      : `activity:${activityId ?? "missing"}`;
  const [materials, setMaterials] = useState<EditorMaterial[]>([]);
  const [newCollectionName, setNewCollectionName] = useState("");
  const [collectionError, setCollectionError] = useState("");
  const [showNewCollection, setShowNewCollection] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [materialsDirty, setMaterialsDirty] = useState(false);
  const [materialErrors, setMaterialErrors] = useState<Record<string, MaterialFieldErrors>>({});
  const [materialFocusRequest, setMaterialFocusRequest] = useState<MaterialFocusRequest | null>(
    null,
  );
  const [recoveredDraft, setRecoveredDraft] = useState(false);
  const [saveState, setSaveState] = useState<
    "saved" | "checking" | "warning" | "saving" | "failed"
  >("saved");
  const [saveError, setSaveError] = useState("");
  const [schedulingError, setSchedulingError] = useState("");
  const [scheduledCreation, setScheduledCreation] = useState<ScheduledCreation | null>(null);
  const [duplicateCheck, setDuplicateCheck] = useState<DuplicateCheckState>({ status: "idle" });
  const [idempotencyKey, setIdempotencyKey] = useState<string>(createUuid);
  const [baseVersion, setBaseVersion] = useState<number | null>(null);
  const [abandonRequest, setAbandonRequest] = useState<ActivityAbandonRequest | null>(null);
  const [discardingChanges, setDiscardingChanges] = useState(false);
  const formElementRef = useRef<HTMLFormElement>(null);
  const saveErrorRef = useRef<HTMLDivElement>(null);
  const scheduleRetryRef = useRef<HTMLButtonElement>(null);
  const materialSectionRef = useRef<HTMLElement>(null);
  const pendingFiles = useRef(new Map<string, File>());
  const initialFileIds = useRef(new Set<string>());
  const initialLinkUrls = useRef(new Map<string, string>());
  const materialsRef = useRef<EditorMaterial[]>([]);
  const saving = useRef(false);
  const focusSaveErrorAfterRender = useRef(false);
  const activeCheckFingerprint = useRef<string | null>(null);
  const queuedSave = useRef<{ fingerprint: string } | null>(null);
  const dirtyRef = useRef(false);
  const abandonRequestRef = useRef<ActivityAbandonRequest | null>(null);
  const discardingChangesRef = useRef(false);

  const form = useForm<EditorFields>({
    resolver: zodResolver(editorFieldsSchema) as Resolver<EditorFields>,
    defaultValues: defaultFields(),
  });
  const watchedFields = useWatch({ control: form.control });
  const options = useQuery({
    queryKey: activityEditorOptionsKey,
    queryFn: loadActivityEditorOptions,
  });
  const activityQuery = useQuery({
    queryKey: activityEditorKey(activityId ?? ""),
    queryFn: () => loadActivityEditor(activityId ?? ""),
    enabled: mode === "edit" && Boolean(activityId),
  });
  const isDirty = form.formState.isDirty || materialsDirty;
  const navigationDirty = isDirty && !discardingChanges;
  materialsRef.current = materials;
  dirtyRef.current = navigationDirty;

  const requestAbandon = useCallback((request: ActivityAbandonRequest): void => {
    if (abandonRequestRef.current) return;
    abandonRequestRef.current = request;
    setAbandonRequest(request);
  }, []);

  const keepEditing = useCallback((): void => {
    if (discardingChangesRef.current) return;
    const request = abandonRequestRef.current;
    abandonRequestRef.current = null;
    setAbandonRequest(null);
    if (request?.restoreFocusTo?.isConnected) {
      window.requestAnimationFrame(() => request.restoreFocusTo?.focus());
    }
  }, []);

  const discardAndProceed = useCallback(
    async (request: ActivityAbandonRequest): Promise<void> => {
      if (discardingChangesRef.current) return;
      discardingChangesRef.current = true;
      setDiscardingChanges(true);
      dirtyRef.current = false;
      setActivityNavigationDirty(false);
      await cleanupNewUploads(materialsRef.current, initialFileIds.current);
      await deleteActivityDraft(draftKey);
      abandonRequestRef.current = null;
      setAbandonRequest(null);
      dirtyRef.current = false;
      request.proceed();
    },
    [draftKey],
  );

  const hydrateFromDocument = useCallback(
    (activity: ActivityDocument) => {
      form.reset(fieldsFromDocument(activity));
      setMaterials(activity.materials.map(toEditorMaterial));
      initialFileIds.current = new Set(
        activity.materials.flatMap((material) =>
          material.kind === "image" || material.kind === "file" ? [material.fileId] : [],
        ),
      );
      initialLinkUrls.current = new Map(
        activity.materials.flatMap((material) =>
          material.kind === "link" ? [[material.id, material.url] as const] : [],
        ),
      );
      setBaseVersion(activity.version);
      setMaterialsDirty(false);
      setSaveState("saved");
      setSaveError("");
      setDuplicateCheck({ status: "idle" });
    },
    [form],
  );

  useEffect(() => {
    document.title = `${mode === "create" ? "Create activity" : "Edit activity"} · MemoKite`;
  }, [mode]);

  useEffect(() => {
    setActivityNavigationDirty(navigationDirty);
    return () => setActivityNavigationDirty(false);
  }, [navigationDirty]);

  useLayoutEffect(() => {
    if (!saveError || !focusSaveErrorAfterRender.current) return;
    focusSaveErrorAfterRender.current = false;
    saveErrorRef.current?.focus();
  }, [saveError]);

  useLayoutEffect(() => {
    if (!materialFocusRequest) return;

    const focusTarget = (): boolean => {
      const card = materialFocusRequest.materialId
        ? document.querySelector<HTMLElement>(
            `[data-material-id="${materialFocusRequest.materialId}"]`,
          )
        : materialSectionRef.current;
      const target = card?.querySelector<HTMLElement>(materialFocusRequest.selector);
      if (
        !target ||
        target.matches(":disabled") ||
        target.getAttribute("aria-disabled") === "true"
      ) {
        return false;
      }
      target.focus();
      return document.activeElement === target;
    };

    if (focusTarget()) return;
    const section = materialSectionRef.current;
    if (!section) return;
    const observer = new MutationObserver(() => {
      if (focusTarget()) observer.disconnect();
    });
    observer.observe(section, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ["aria-disabled", "contenteditable", "disabled"],
    });
    return () => observer.disconnect();
  }, [materialFocusRequest]);

  useEffect(() => {
    if (
      initialized ||
      !options.data ||
      options.isLoading ||
      (mode === "edit" && activityQuery.isLoading)
    ) {
      return;
    }
    if (options.isError || (mode === "edit" && (activityQuery.isError || !activityQuery.data))) {
      return;
    }
    let active = true;
    const serverDocument = mode === "edit" ? activityQuery.data : undefined;
    if (serverDocument) {
      initialFileIds.current = new Set(
        serverDocument.materials.flatMap((material) =>
          material.kind === "image" || material.kind === "file" ? [material.fileId] : [],
        ),
      );
      initialLinkUrls.current = new Map(
        serverDocument.materials.flatMap((material) =>
          material.kind === "link" ? [[material.id, material.url] as const] : [],
        ),
      );
    }
    void readActivityDraft(draftKey)
      .catch(() => null)
      .then((draft) => {
        if (!active) return;
        const canRecover =
          draft &&
          (mode === "create" || (serverDocument && draft.baseVersion === serverDocument.version));
        if (canRecover) {
          form.reset(draft.fields);
          setMaterials(draft.materials.map(toEditorMaterial));
          setIdempotencyKey(draft.idempotencyKey);
          setScheduledCreation(calendarCreationIntent ? (draft.scheduledCreation ?? null) : null);
          setBaseVersion(draft.baseVersion);
          setRecoveredDraft(true);
          setMaterialsDirty(true);
        } else if (serverDocument) {
          hydrateFromDocument(serverDocument);
        } else {
          form.reset(defaultFields());
          setMaterials([]);
          setBaseVersion(null);
        }
        setInitialized(true);
      });
    return () => {
      active = false;
    };
  }, [
    activityQuery.data,
    activityQuery.isError,
    activityQuery.isLoading,
    calendarCreationIntent,
    draftKey,
    form,
    hydrateFromDocument,
    initialized,
    mode,
    options.isError,
    options.isLoading,
    options.data,
  ]);

  useEffect(() => {
    if (!initialized || !isDirty) return;
    const timer = window.setTimeout(() => {
      if (discardingChangesRef.current) return;
      const serializable = serializableMaterials(materials);
      const draft: ActivityDraft = {
        key: draftKey,
        baseVersion,
        idempotencyKey,
        ...(scheduledCreation ? { scheduledCreation } : {}),
        fields: normalizeFields(form.getValues()),
        materials: serializable,
        savedAt: new Date().toISOString(),
      };
      void writeActivityDraft(draft);
    }, 450);
    return () => window.clearTimeout(timer);
  }, [
    baseVersion,
    draftKey,
    form,
    idempotencyKey,
    initialized,
    isDirty,
    materials,
    scheduledCreation,
    watchedFields,
  ]);

  useEffect(() => {
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirtyRef.current) return;
      event.preventDefault();
      event.returnValue = "";
    };
    const protectInternalLink = (event: MouseEvent) => {
      if (!dirtyRef.current) return;
      const target = event.target;
      const anchor =
        target instanceof Element ? target.closest<HTMLAnchorElement>("a[href]") : null;
      if (
        !anchor ||
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }
      if (anchor.getAttribute("data-preserve-editor") === "true") return;
      if (anchor.hasAttribute("download")) return;
      const targetName = anchor.getAttribute("target");
      if (targetName && targetName.toLowerCase() !== "_self") return;
      const destination = new URL(anchor.href, window.location.href);
      if (
        destination.origin !== window.location.origin ||
        (destination.pathname === window.location.pathname &&
          destination.search === window.location.search)
      ) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      requestAbandon({
        proceed: () => anchor.click(),
        restoreFocusTo: anchor,
      });
    };
    window.addEventListener("beforeunload", beforeUnload);
    document.addEventListener("click", protectInternalLink, true);
    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
      document.removeEventListener("click", protectInternalLink, true);
    };
  }, [requestAbandon]);

  useEffect(
    () =>
      registerActivityHistoryNavigationHandler(({ proceed }) =>
        requestAbandon({ proceed, restoreFocusTo: null }),
      ),
    [requestAbandon],
  );

  const markMaterialsDirty = () => {
    setMaterialsDirty(true);
    setSaveState("saved");
    setSaveError("");
  };

  const clearDuplicateCheck = () => {
    setDuplicateCheck({ status: "idle" });
  };

  const collectionMutation = useMutation({
    mutationFn: () => createCollection(newCollectionName),
    onSuccess: async ({ collection }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: activityEditorOptionsKey }),
        queryClient.invalidateQueries({ queryKey: libraryQueryRoot }),
      ]);
      form.setValue("collectionId", collection.id, { shouldDirty: true });
      setNewCollectionName("");
      setCollectionError("");
      setShowNewCollection(false);
    },
    onError: (error) => {
      setCollectionError(
        error instanceof ApiError
          ? (error.problem?.fieldErrors?.["name"]?.[0] ?? error.problem?.title ?? error.message)
          : "The collection could not be created.",
      );
    },
  });

  const updateMaterial = (id: string, update: (material: EditorMaterial) => EditorMaterial) => {
    setMaterials((current) =>
      current.map((material) => (material.id === id ? update(material) : material)),
    );
    setMaterialErrors((current) => {
      if (!(id in current)) return current;
      const next = { ...current };
      delete next[id];
      return next;
    });
    markMaterialsDirty();
  };

  const addMaterial = (kind: EditorMaterial["kind"]) => {
    const id = createUuid();
    const material: EditorMaterial =
      kind === "link"
        ? {
            id,
            kind,
            url: "",
            title: "",
            provider: "",
            resourceType: "article",
            thumbnailUrl: null,
            durationMinutes: null,
            metadataStatus: "idle",
          }
        : kind === "rich_text"
          ? { id, kind, document: emptyRichText }
          : {
              id,
              kind,
              fileId: null,
              file: null,
              altText: "",
              decorative: false,
              uploadStatus: "empty",
              uploadProgress: 0,
              uploadError: "",
              previewUrl: null,
            };
    setMaterials((current) => [...current, material]);
    clearDuplicateCheck();
    setMaterialFocusRequest((current) => ({
      generation: (current?.generation ?? 0) + 1,
      materialId: id,
      selector: "input, [contenteditable], button",
    }));
    markMaterialsDirty();
  };

  const removeMaterial = async (material: EditorMaterial) => {
    if (
      isUploadMaterial(material) &&
      material.fileId &&
      !initialFileIds.current.has(material.fileId)
    ) {
      await removeUpload(material.fileId).catch(() => undefined);
    }
    if (isUploadMaterial(material) && material.previewUrl?.startsWith("blob:")) {
      URL.revokeObjectURL(material.previewUrl);
    }
    pendingFiles.current.delete(material.id);
    setMaterials((current) => current.filter(({ id }) => id !== material.id));
    clearDuplicateCheck();
    markMaterialsDirty();
  };

  const moveMaterial = (index: number, direction: -1 | 1) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= materials.length) return;
    setMaterials((current) => {
      const next = [...current];
      const [moved] = next.splice(index, 1);
      if (!moved) return current;
      next.splice(nextIndex, 0, moved);
      return next;
    });
    markMaterialsDirty();
    const materialId = materials[index]?.id;
    if (materialId) {
      setMaterialFocusRequest((current) => ({
        generation: (current?.generation ?? 0) + 1,
        materialId,
        selector: `[data-reorder="${direction === -1 ? "up" : "down"}"]`,
      }));
    }
  };

  const startUpload = async (
    materialId: string,
    kind: "image" | "file",
    file: File,
  ): Promise<void> => {
    const previous = materials.find((material) => material.id === materialId);
    const previousFileId = previous && isUploadMaterial(previous) ? previous.fileId : null;
    const previousPreview = previous && isUploadMaterial(previous) ? previous.previewUrl : null;
    if (previousPreview?.startsWith("blob:")) URL.revokeObjectURL(previousPreview);
    pendingFiles.current.set(materialId, file);
    const previewUrl = kind === "image" ? URL.createObjectURL(file) : null;
    updateMaterial(materialId, (material) =>
      isUploadMaterial(material)
        ? {
            ...material,
            file: {
              id: createUuid(),
              kind,
              originalFilename: file.name,
              contentType: file.type,
              size: file.size,
              status: "pending",
              contentUrl: null,
            },
            fileId: null,
            uploadStatus: "uploading",
            uploadProgress: 0,
            uploadError: "",
            previewUrl,
          }
        : material,
    );
    try {
      createUploadInputSchema.parse({
        kind,
        originalFilename: file.name,
        contentType: file.type,
        size: file.size,
        checksum: "0".repeat(64),
      });
      const uploaded = await uploadFile(
        file,
        kind,
        (progress) => {
          if (pendingFiles.current.get(materialId) !== file) return;
          updateMaterial(materialId, (material) =>
            isUploadMaterial(material)
              ? { ...material, uploadProgress: progress, uploadStatus: "uploading" }
              : material,
          );
        },
        (pending) => {
          if (pendingFiles.current.get(materialId) !== file) return false;
          updateMaterial(materialId, (material) =>
            isUploadMaterial(material)
              ? { ...material, fileId: pending.id, file: pending }
              : material,
          );
          return true;
        },
      );
      if (pendingFiles.current.get(materialId) !== file) {
        await removeUpload(uploaded.id).catch(() => undefined);
        return;
      }
      updateMaterial(materialId, (material) =>
        isUploadMaterial(material)
          ? {
              ...material,
              fileId: uploaded.id,
              file: uploaded,
              uploadStatus: "ready",
              uploadProgress: 100,
              uploadError: "",
            }
          : material,
      );
      if (
        previousFileId &&
        previousFileId !== uploaded.id &&
        !initialFileIds.current.has(previousFileId)
      ) {
        await removeUpload(previousFileId).catch(() => undefined);
      }
    } catch (error) {
      if (pendingFiles.current.get(materialId) !== file) return;
      updateMaterial(materialId, (material) =>
        isUploadMaterial(material)
          ? {
              ...material,
              uploadStatus: "failed",
              uploadError: errorMessage(error, "The upload failed. Retry or remove it."),
            }
          : material,
      );
    }
  };

  const inspectLink = async (id: string) => {
    const material = materials.find((candidate) => candidate.id === id);
    if (!material || material.kind !== "link") return;
    const parsed = safeHttpUrlSchema.safeParse(material.url);
    if (!parsed.success) {
      updateMaterial(id, (current) =>
        current.kind === "link"
          ? {
              ...current,
              metadataStatus: "failed",
              metadataError: parsed.error.issues[0]?.message ?? "Enter a complete URL.",
            }
          : current,
      );
      return;
    }
    updateMaterial(id, (current) =>
      current.kind === "link"
        ? { ...current, metadataStatus: "loading", metadataError: "" }
        : current,
    );
    try {
      const metadata = await fetchResourceMetadata(parsed.data);
      if (metadata.url !== material.url) clearDuplicateCheck();
      updateMaterial(id, (current) =>
        current.kind === "link"
          ? { ...current, ...metadata, metadataStatus: "idle", metadataError: "" }
          : current,
      );
      if (!form.getValues("title").trim() && metadata.title.trim()) {
        form.setValue("title", metadata.title);
      }
    } catch (error) {
      updateMaterial(id, (current) =>
        current.kind === "link"
          ? {
              ...current,
              metadataStatus: "failed",
              metadataError: errorMessage(
                error,
                "Details could not be fetched. Enter them manually or retry.",
              ),
            }
          : current,
      );
    }
  };

  const scheduleCreatedActivity = async (creation: ScheduledCreation): Promise<void> => {
    if (!calendarCreationIntent) return;
    setSaveState("saving");
    setSaveError("");
    setSchedulingError("");
    try {
      await createActivitySession(creation.activityId, {
        idempotencyKey: creation.schedulingIdempotencyKey,
        scheduledDate: calendarCreationIntent.scheduledDate,
        scheduledTime: null,
        allowDuplicate: false,
      });
    } catch {
      setSaveState("failed");
      setSchedulingError(
        `${creation.title} was saved, but it was not added to ${formatFullDate(
          calendarCreationIntent.scheduledDate,
        )}. Try adding it again; no duplicate activity will be created.`,
      );
      window.requestAnimationFrame(() => scheduleRetryRef.current?.focus());
      return;
    }
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: calendarQueryRoot }),
      queryClient.invalidateQueries({ queryKey: libraryQueryRoot }),
      queryClient.invalidateQueries({ queryKey: activityEditorOptionsKey }),
      queryClient.invalidateQueries({ queryKey: ["activity-detail", creation.activityId] }),
      queryClient.invalidateQueries({ queryKey: ["today"] }),
    ]).catch(() => undefined);
    await deleteActivityDraft(draftKey).catch(() => undefined);
    form.reset(normalizeFields(form.getValues()));
    setMaterialsDirty(false);
    setActivityNavigationDirty(false);
    void navigate(calendarCreationIntent.returnTo, {
      replace: true,
      state: {
        calendarActivityScheduled: {
          id: creation.activityId,
          title: creation.title,
          date: calendarCreationIntent.scheduledDate,
        },
      },
    });
  };

  const retryScheduledCreation = async (): Promise<void> => {
    if (!scheduledCreation || saving.current) return;
    saving.current = true;
    try {
      await scheduleCreatedActivity(scheduledCreation);
    } finally {
      saving.current = false;
    }
  };

  const save = async ({ skipDuplicateCheck = false }: SaveOptions = {}) => {
    if (scheduledCreation) {
      await retryScheduledCreation();
      return;
    }
    if (saving.current) {
      const activeFingerprint = activeCheckFingerprint.current;
      const currentFingerprint = duplicateCheckFingerprint(
        editorLinksRequiringDuplicateCheck(materialsRef.current, mode, initialLinkUrls.current),
      );
      if (activeFingerprint && currentFingerprint !== activeFingerprint) {
        queuedSave.current = { fingerprint: currentFingerprint };
      }
      return;
    }
    setSaveError("");
    form.clearErrors();
    const raw = {
      ...normalizeFields(form.getValues()),
      materials: materials.map(materialInput),
    };
    const parsed = activityWriteSchema.safeParse(raw);
    if (!parsed.success) {
      const nextMaterialErrors = collectMaterialErrors(parsed.error.issues, materials);
      setMaterialErrors(nextMaterialErrors);
      focusFirstIssue(parsed.error.issues[0], nextMaterialErrors);
      setSaveState("failed");
      setSaveError("Check the highlighted details. Your draft is still safe.");
      return;
    }
    setMaterialErrors({});
    let writeToSave = parsed.data;

    const links = linksRequiringDuplicateCheck(parsed.data, mode, initialLinkUrls.current);
    const fingerprint = duplicateCheckFingerprint(links);
    const duplicateAcknowledged =
      duplicateCheck.status === "duplicate" && duplicateCheck.fingerprint === fingerprint;
    const failureAcknowledged =
      skipDuplicateCheck &&
      duplicateCheck.status === "failed" &&
      duplicateCheck.fingerprint === fingerprint;

    saving.current = true;
    try {
      if (links.length > 0 && !duplicateAcknowledged && !failureAcknowledged) {
        const materialIds = links.map(({ materialId }) => materialId);
        activeCheckFingerprint.current = fingerprint;
        setSaveState("checking");
        setDuplicateCheck({ status: "checking", fingerprint, materialIds });
        let checked: DuplicateLinkCheckResponse;
        try {
          checked = await checkDuplicateActivityLinks({
            links,
            excludeActivityId: mode === "edit" ? (activityId ?? null) : null,
          });
        } catch (error) {
          if (
            duplicateCheckFingerprint(
              editorLinksRequiringDuplicateCheck(
                materialsRef.current,
                mode,
                initialLinkUrls.current,
              ),
            ) !== fingerprint
          ) {
            setSaveState("saved");
            setDuplicateCheck({ status: "idle" });
            return;
          }
          setSaveState("failed");
          setDuplicateCheck({
            status: "failed",
            fingerprint,
            materialIds,
            message: errorMessage(
              error,
              "We could not check this link. Retry the check or save without checking.",
            ),
          });
          return;
        }

        const currentFingerprint = duplicateCheckFingerprint(
          editorLinksRequiringDuplicateCheck(materialsRef.current, mode, initialLinkUrls.current),
        );
        if (currentFingerprint !== fingerprint) {
          setSaveState("saved");
          setDuplicateCheck({ status: "idle" });
          return;
        }

        const currentWrite = activityWriteSchema.safeParse({
          ...normalizeFields(form.getValues()),
          materials: materialsRef.current.map(materialInput),
        });
        if (!currentWrite.success) {
          setMaterialErrors(collectMaterialErrors(currentWrite.error.issues, materialsRef.current));
          setSaveState("failed");
          setSaveError("The activity changed while links were checked. Check it and save again.");
          return;
        }
        writeToSave = currentWrite.data;

        const matchesByMaterial = Object.fromEntries(
          checked.links
            .filter(({ matches }) => matches.length > 0)
            .map(({ materialId, matches }) => [materialId, matches]),
        );
        const firstDuplicateMaterialId = links.find(
          ({ materialId }) => matchesByMaterial[materialId],
        )?.materialId;
        if (firstDuplicateMaterialId) {
          setSaveState("warning");
          setDuplicateCheck({ status: "duplicate", fingerprint, matchesByMaterial });
          setMaterialFocusRequest((current) => ({
            generation: (current?.generation ?? 0) + 1,
            materialId: firstDuplicateMaterialId,
            selector: "[data-duplicate-warning]",
          }));
          return;
        }
        setDuplicateCheck({ status: "idle" });
      }

      setSaveState("saving");
      const result =
        mode === "create"
          ? await createActivity({ ...writeToSave, idempotencyKey })
          : await updateActivity(activityId ?? "", {
              ...writeToSave,
              version: baseVersion ?? 0,
            });
      if (mode === "create") {
        if (calendarCreationIntent) {
          const creation: ScheduledCreation = {
            activityId: result.activity.id,
            title: result.activity.title,
            schedulingIdempotencyKey: createUuid(),
          };
          setScheduledCreation(creation);
          await writeActivityDraft({
            key: draftKey,
            baseVersion,
            idempotencyKey,
            scheduledCreation: creation,
            fields: normalizeFields(form.getValues()),
            materials: serializableMaterials(materialsRef.current),
            savedAt: new Date().toISOString(),
          }).catch(() => undefined);
          await scheduleCreatedActivity(creation);
          return;
        }
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: libraryQueryRoot }),
          queryClient.invalidateQueries({ queryKey: activityEditorOptionsKey }),
          queryClient.invalidateQueries({ queryKey: ["today"] }),
        ]);
        await deleteActivityDraft(draftKey);
        form.reset(normalizeFields(form.getValues()));
        setMaterialsDirty(false);
        void navigate(returnTo, {
          replace: true,
          state: {
            activityCreated: {
              id: result.activity.id,
            },
          },
        });
      } else {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: libraryQueryRoot }),
          queryClient.invalidateQueries({ queryKey: activityEditorOptionsKey }),
          queryClient.invalidateQueries({ queryKey: activityDetailKey(result.activity.id) }),
          queryClient.invalidateQueries({ queryKey: ["today"] }),
        ]);
        await deleteActivityDraft(draftKey);
        queryClient.setQueryData(activityEditorKey(result.activity.id), result.activity);
        setActivityNavigationDirty(false);
        void navigate(detailReturnTo ?? returnTo, {
          replace: true,
          state: detailReturnTo ? createActivityReturnState(detailOriginReturnTo) : undefined,
        });
      }
    } catch (error) {
      setSaveState("failed");
      setSaveError(errorMessage(error, "Your activity could not be saved. Your draft is safe."));
      const focusedField = error instanceof ApiError && applyServerFieldErrors(error);
      if (!focusedField) {
        focusSaveErrorAfterRender.current = true;
      }
    } finally {
      activeCheckFingerprint.current = null;
      saving.current = false;
      const nextSave = queuedSave.current;
      queuedSave.current = null;
      if (
        nextSave &&
        duplicateCheckFingerprint(
          editorLinksRequiringDuplicateCheck(materialsRef.current, mode, initialLinkUrls.current),
        ) === nextSave.fingerprint
      ) {
        window.setTimeout(() => {
          formElementRef.current?.requestSubmit();
        }, 0);
      }
    }
  };

  const focusFirstIssue = (
    issue: { path: PropertyKey[]; message: string } | undefined,
    nextMaterialErrors: Record<string, MaterialFieldErrors> = materialErrors,
  ) => {
    if (!issue) return;
    const field = issue.path[0]?.toString();
    if (
      field === "title" ||
      field === "summary" ||
      field === "collectionId" ||
      field === "estimatedMinutes"
    ) {
      form.setError(field, { message: issue.message });
      form.setFocus(field);
    } else if (field === "materials") {
      const materialIndex = Number(issue.path[1]);
      const material = Number.isInteger(materialIndex) ? materials[materialIndex] : undefined;
      const errorField = material ? materialIssueField(issue, material) : undefined;
      setSaveError(issue.message);
      setMaterialFocusRequest((current) => ({
        generation: (current?.generation ?? 0) + 1,
        materialId: material?.id ?? null,
        selector:
          errorField && material && nextMaterialErrors[material.id]?.[errorField]
            ? `[data-material-field="${errorField}"]`
            : "input, [contenteditable], button",
      }));
    } else {
      setSaveError(issue.message);
      materialSectionRef.current
        ?.querySelector<HTMLElement>("input, [contenteditable], button")
        ?.focus();
    }
  };

  const applyServerFieldErrors = (error: ApiError): boolean => {
    const fieldErrors = error.problem?.fieldErrors;
    if (!fieldErrors) return false;
    const first = Object.entries(fieldErrors)[0];
    if (!first) return false;
    focusFirstIssue({ path: first[0].split("."), message: first[1][0] ?? error.message });
    return true;
  };

  const cancel = (restoreFocusTo: HTMLElement) => {
    const request: ActivityAbandonRequest = {
      proceed: () =>
        void navigate(detailReturnTo ?? returnTo, {
          state: detailReturnTo ? createActivityReturnState(detailOriginReturnTo) : undefined,
        }),
      restoreFocusTo,
    };
    if (isDirty) {
      requestAbandon(request);
      return;
    }
    void discardAndProceed(request);
  };

  if (options.isError || (mode === "edit" && activityQuery.isError)) {
    return (
      <EditorLoadError
        onRetry={() => void Promise.all([options.refetch(), activityQuery.refetch()])}
      />
    );
  }
  if (options.isLoading || !options.data || !initialized) {
    return <EditorLoading />;
  }
  const editorOptions = options.data;
  const savePending = saveState === "checking" || saveState === "saving";
  const scheduleActionDate = calendarCreationIntent
    ? formatScheduleActionDate(calendarCreationIntent.scheduledDate)
    : null;
  const saveButtonText =
    saveState === "checking"
      ? "Checking…"
      : saveState === "saving"
        ? "Saving…"
        : scheduledCreation && scheduleActionDate
          ? `Retry add to ${scheduleActionDate}`
          : duplicateCheck.status === "duplicate"
            ? calendarCreationIntent && scheduleActionDate
              ? `Save anyway and add to ${scheduleActionDate}`
              : "Save anyway"
            : calendarCreationIntent && scheduleActionDate
              ? `Save and add to ${scheduleActionDate}`
              : detailReturnTo
                ? "Save and return to activity"
                : "Save and return to Library";

  return (
    <AppShell mainClassName="activity-editor-screen">
      <header className="activity-editor-topbar">
        <button
          type="button"
          className="back-button"
          onClick={(event) => cancel(event.currentTarget)}
        >
          <ArrowLeft size={17} /> Cancel
        </button>
        <div className={`editor-save-status status-${saveState}`} aria-live="polite">
          {schedulingError
            ? "Saved, not added"
            : scheduledCreation
              ? "Saved, ready to retry"
              : saveState === "saving"
                ? "Saving…"
                : saveState === "checking"
                  ? "Checking links…"
                  : saveState === "warning"
                    ? "Duplicate link found"
                    : saveState === "failed"
                      ? "Failed to save"
                      : isDirty
                        ? "Unsaved changes"
                        : "Saved"}
        </div>
        <button
          type="submit"
          form={activityEditorFormId}
          className="primary-button"
          disabled={savePending}
        >
          <Save size={16} />
          {saveButtonText}
        </button>
      </header>

      <section className="activity-editor-main" aria-labelledby="activity-editor-heading">
        <div className="activity-editor-heading">
          <div className="activity-editor-heading-title">
            <span className="section-kicker">
              {mode === "create" ? "New activity" : "Activity editor"}
            </span>
            <h1 id="activity-editor-heading">
              {mode === "create" ? "Create a learning activity" : "Edit your learning activity"}
            </h1>
          </div>
        </div>

        {calendarCreationIntent ? (
          <div className="activity-editor-schedule-intent" role="status">
            <CalendarDays size={17} aria-hidden="true" />
            <span>
              {scheduledCreation ? (
                <>
                  <strong>{scheduledCreation.title}</strong> is saved, but it still needs to be
                  added to <strong>{formatFullDate(calendarCreationIntent.scheduledDate)}</strong>.
                </>
              ) : (
                <>
                  This activity will be added to{" "}
                  <strong>{formatFullDate(calendarCreationIntent.scheduledDate)}</strong> when you
                  save.
                </>
              )}
            </span>
            <small>Any time</small>
          </div>
        ) : null}
        {recoveredDraft ? (
          <div className="draft-recovery-note" role="status">
            <RotateCcw size={16} />
            Your unsaved draft was recovered.
            <button
              type="button"
              onClick={() => {
                if (activityQuery.data) hydrateFromDocument(activityQuery.data);
                else {
                  form.reset(defaultFields());
                  setMaterials([]);
                  setMaterialsDirty(false);
                }
                setRecoveredDraft(false);
                void deleteActivityDraft(draftKey);
              }}
            >
              Discard recovered draft
            </button>
          </div>
        ) : null}
        {saveError ? (
          <div ref={saveErrorRef} className="editor-error-banner" role="alert" tabIndex={-1}>
            {saveError}
          </div>
        ) : null}
        {schedulingError ? (
          <div className="editor-error-banner editor-scheduling-error" role="alert">
            <span>{schedulingError}</span>
            <button
              ref={scheduleRetryRef}
              type="button"
              className="soft-button"
              disabled={savePending}
              onClick={() => void retryScheduledCreation()}
            >
              <CalendarDays size={15} aria-hidden="true" />
              {savePending ? "Adding…" : `Try adding to ${scheduleActionDate ?? "Calendar"} again`}
            </button>
          </div>
        ) : null}

        <form
          id={activityEditorFormId}
          ref={formElementRef}
          className="activity-editor-form"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <section className="editor-section" aria-labelledby="activity-identity-heading">
            <div className="editor-section-number">1</div>
            <div className="editor-section-content">
              <h2 id="activity-identity-heading">Activity identity</h2>
              <p>Give this learning intention a clear, memorable shape.</p>
              <label className="editor-field editor-field-wide">
                <span>Activity title *</span>
                <input
                  {...form.register("title")}
                  aria-invalid={Boolean(form.formState.errors.title)}
                  aria-describedby={
                    form.formState.errors.title ? "activity-title-error" : undefined
                  }
                  placeholder="e.g. Understand practical typography"
                />
                {form.formState.errors.title ? (
                  <small id="activity-title-error" className="field-error" role="alert">
                    {form.formState.errors.title.message}
                  </small>
                ) : null}
              </label>
              <label className="editor-field editor-field-wide">
                <span>Learning intention</span>
                <textarea
                  {...form.register("summary")}
                  rows={3}
                  aria-invalid={Boolean(form.formState.errors.summary)}
                  placeholder="What would you like to understand or be able to do?"
                />
                {form.formState.errors.summary ? (
                  <small className="field-error" role="alert">
                    {form.formState.errors.summary.message}
                  </small>
                ) : null}
              </label>
              <div className="editor-field-grid">
                <label className="editor-field">
                  <span>Collection</span>
                  <select
                    aria-label="Collection"
                    value={watchedFields.collectionId ?? ""}
                    {...form.register("collectionId", {
                      setValueAs: (value: unknown) =>
                        typeof value === "string" && value ? value : null,
                    })}
                    onChange={(event) => {
                      if (event.target.value === "__new") {
                        setShowNewCollection(true);
                        form.setValue("collectionId", null, { shouldDirty: true });
                      } else {
                        setShowNewCollection(false);
                        form.setValue("collectionId", event.target.value || null, {
                          shouldDirty: true,
                        });
                      }
                    }}
                  >
                    <option value="">Uncollected</option>
                    {editorOptions.collections.map((collection) => (
                      <option key={collection.id} value={collection.id}>
                        {collection.name}
                      </option>
                    ))}
                    <option value="__new">Create a new collection…</option>
                  </select>
                </label>
                <label className="editor-field">
                  <span>Estimated learning time *</span>
                  <span className="field-with-suffix">
                    <input
                      type="number"
                      min={1}
                      max={1440}
                      {...form.register("estimatedMinutes", { valueAsNumber: true })}
                      aria-invalid={Boolean(form.formState.errors.estimatedMinutes)}
                    />
                    <i>minutes</i>
                  </span>
                  {form.formState.errors.estimatedMinutes ? (
                    <small className="field-error" role="alert">
                      {form.formState.errors.estimatedMinutes.message}
                    </small>
                  ) : null}
                </label>
              </div>
              {showNewCollection ? (
                <div className="inline-collection" aria-label="Create a new collection">
                  <label className="editor-field">
                    <span>New collection name</span>
                    <input
                      value={newCollectionName}
                      onChange={(event) => {
                        setNewCollectionName(event.target.value);
                        setCollectionError("");
                      }}
                    />
                  </label>
                  <button
                    type="button"
                    className="soft-button"
                    disabled={collectionMutation.isPending}
                    onClick={() => collectionMutation.mutate()}
                  >
                    <Plus size={15} />{" "}
                    {collectionMutation.isPending ? "Creating…" : "Create and select"}
                  </button>
                  {collectionError ? (
                    <p className="field-error" role="alert">
                      {collectionError}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          </section>

          <section
            ref={materialSectionRef}
            className="editor-section"
            aria-labelledby="learning-material-heading"
          >
            <div className="editor-section-number">2</div>
            <div className="editor-section-content">
              <div className="materials-section-heading">
                <div>
                  <h2 id="learning-material-heading">Learning materials</h2>
                  <p>Mix links, your own notes, images, and files in the order you need them.</p>
                </div>
                <span className="material-count">
                  {materials.length} {materials.length === 1 ? "item" : "items"}
                </span>
              </div>
              <div className="material-list">
                {materials.map((material, index) => (
                  <MaterialCard
                    key={material.id}
                    material={material}
                    index={index}
                    count={materials.length}
                    onUpdate={(update) => updateMaterial(material.id, update)}
                    onRemove={() => void removeMaterial(material)}
                    onMove={(direction) => moveMaterial(index, direction)}
                    onInspect={() => void inspectLink(material.id)}
                    onUrlChange={(url) => {
                      clearDuplicateCheck();
                      updateMaterial(material.id, (current) =>
                        current.kind === "link" ? linkWithRecognizedUrl(current, url) : current,
                      );
                    }}
                    duplicateState={duplicateStateForMaterial(duplicateCheck, material.id)}
                    onRetryDuplicateCheck={() => void save()}
                    onSaveWithoutCheck={() => void save({ skipDuplicateCheck: true })}
                    onFile={(file) =>
                      void startUpload(
                        material.id,
                        material.kind === "image" ? "image" : "file",
                        file,
                      )
                    }
                    onRetry={() => {
                      const file = pendingFiles.current.get(material.id);
                      if (file && isUploadMaterial(material)) {
                        void startUpload(material.id, material.kind, file);
                      } else {
                        document
                          .querySelector<HTMLInputElement>(
                            `[data-material-id="${material.id}"] input[type="file"]`,
                          )
                          ?.click();
                      }
                    }}
                    errors={materialErrors[material.id] ?? {}}
                  />
                ))}
              </div>
              <div className="material-add-buttons" aria-label="Add learning material">
                <button type="button" onClick={() => addMaterial("link")}>
                  <LinkIcon size={17} /> Link
                </button>
                <button type="button" onClick={() => addMaterial("rich_text")}>
                  <FileText size={17} /> Rich text
                </button>
                <button type="button" onClick={() => addMaterial("image")}>
                  <Image size={17} /> Image
                </button>
                <button type="button" onClick={() => addMaterial("file")}>
                  <Upload size={17} /> File
                </button>
              </div>
              <div className="material-order-hint">
                <span>Materials stay in the order shown above.</span>
                <strong>Use the arrow controls to reorder</strong>
              </div>
            </div>
          </section>
        </form>

        <div className="activity-editor-footer">
          <button
            type="button"
            className="soft-button"
            onClick={(event) => cancel(event.currentTarget)}
          >
            Cancel
          </button>
          <button
            type="submit"
            form={activityEditorFormId}
            className="primary-button"
            disabled={savePending}
          >
            <Save size={16} /> {saveButtonText}
          </button>
        </div>
      </section>
      {abandonRequest ? (
        <ActivityAbandonDialog
          discarding={discardingChanges}
          onKeepEditing={keepEditing}
          onDiscard={() => void discardAndProceed(abandonRequest)}
        />
      ) : null}
    </AppShell>
  );
}

function MaterialCard({
  material,
  index,
  count,
  onUpdate,
  onRemove,
  onMove,
  onInspect,
  onUrlChange,
  duplicateState,
  onRetryDuplicateCheck,
  onSaveWithoutCheck,
  onFile,
  onRetry,
  errors,
}: {
  material: EditorMaterial;
  index: number;
  count: number;
  onUpdate: (update: (material: EditorMaterial) => EditorMaterial) => void;
  onRemove: () => void;
  onMove: (direction: -1 | 1) => void;
  onInspect: () => void;
  onUrlChange: (url: string) => void;
  duplicateState: MaterialDuplicateState;
  onRetryDuplicateCheck: () => void;
  onSaveWithoutCheck: () => void;
  onFile: (file: File) => void;
  onRetry: () => void;
  errors: MaterialFieldErrors;
}) {
  return (
    <article className="material-card" data-material-id={material.id}>
      <div className="material-card-heading">
        <div>
          {material.kind === "link" ? <LinkIcon size={16} /> : null}
          {material.kind === "rich_text" ? <FileText size={16} /> : null}
          {material.kind === "image" ? <Image size={16} /> : null}
          {material.kind === "file" ? <Upload size={16} /> : null}
          <strong>
            {material.kind === "rich_text"
              ? "Rich text"
              : material.kind.charAt(0).toUpperCase() + material.kind.slice(1)}
          </strong>
          <span>Block {(index + 1).toString()}</span>
        </div>
        <div className="material-actions">
          <button
            type="button"
            data-reorder="up"
            aria-label={`Move block ${(index + 1).toString()} up`}
            disabled={index === 0}
            onClick={() => onMove(-1)}
          >
            <ArrowUp size={15} />
          </button>
          <button
            type="button"
            data-reorder="down"
            aria-label={`Move block ${(index + 1).toString()} down`}
            disabled={index === count - 1}
            onClick={() => onMove(1)}
          >
            <ArrowDown size={15} />
          </button>
          <button
            type="button"
            aria-label={`Remove block ${(index + 1).toString()}`}
            onClick={onRemove}
          >
            <Trash2 size={15} />
          </button>
        </div>
      </div>
      {material.kind === "link" ? (
        <LinkMaterialEditor
          material={material}
          onUpdate={onUpdate}
          onInspect={onInspect}
          onUrlChange={onUrlChange}
          duplicateState={duplicateState}
          onRetryDuplicateCheck={onRetryDuplicateCheck}
          onSaveWithoutCheck={onSaveWithoutCheck}
          errors={errors}
        />
      ) : material.kind === "rich_text" ? (
        <RichTextEditor
          value={material.document}
          error={errors["document"]}
          onChange={(document) =>
            onUpdate((current) =>
              current.kind === "rich_text" ? { ...current, document } : current,
            )
          }
        />
      ) : (
        <UploadMaterialEditor
          material={material}
          onUpdate={onUpdate}
          onFile={onFile}
          onRetry={onRetry}
          errors={errors}
        />
      )}
    </article>
  );
}

function LinkMaterialEditor({
  material,
  onUpdate,
  onInspect,
  onUrlChange,
  duplicateState,
  onRetryDuplicateCheck,
  onSaveWithoutCheck,
  errors,
}: {
  material: LinkEditorMaterial;
  onUpdate: (update: (material: EditorMaterial) => EditorMaterial) => void;
  onInspect: () => void;
  onUrlChange: (url: string) => void;
  duplicateState: MaterialDuplicateState;
  onRetryDuplicateCheck: () => void;
  onSaveWithoutCheck: () => void;
  errors: MaterialFieldErrors;
}) {
  const field = <K extends keyof LinkEditorMaterial>(key: K, value: LinkEditorMaterial[K]) =>
    onUpdate((current) => (current.kind === "link" ? { ...current, [key]: value } : current));
  return (
    <div className="link-material-fields">
      <label className="editor-field editor-field-wide">
        <span>Resource URL *</span>
        <span className="link-inspection-row">
          <input
            type="url"
            data-material-field="url"
            value={material.url}
            aria-invalid={Boolean(errors["url"])}
            aria-describedby={urlDescriptionIds(material.id, errors["url"], duplicateState)}
            onChange={(event) => onUrlChange(event.target.value)}
            placeholder="https://…"
          />
          <button
            type="button"
            className="soft-button"
            disabled={material.metadataStatus === "loading"}
            onClick={onInspect}
          >
            {material.metadataStatus === "loading" ? "Fetching…" : "Fetch details"}
          </button>
        </span>
        {errors["url"] ? (
          <small id={`${material.id}-url-error`} className="field-error">
            {errors["url"]}
          </small>
        ) : null}
      </label>
      <DuplicateLinkFeedback
        materialId={material.id}
        state={duplicateState}
        onRetry={onRetryDuplicateCheck}
        onSaveWithoutCheck={onSaveWithoutCheck}
      />
      {material.metadataStatus === "failed" ? (
        <div className="material-operation-error" role="alert">
          {material.metadataError}
          <button type="button" onClick={onInspect}>
            Retry
          </button>
        </div>
      ) : null}
      <div className="editor-field-grid">
        <label className="editor-field">
          <span>Resource title *</span>
          <input
            data-material-field="title"
            value={material.title}
            aria-invalid={Boolean(errors["title"])}
            onChange={(event) => field("title", event.target.value)}
          />
          {errors["title"] ? <small className="field-error">{errors["title"]}</small> : null}
        </label>
        <label className="editor-field">
          <span>Provider *</span>
          <input
            data-material-field="provider"
            value={material.provider}
            aria-invalid={Boolean(errors["provider"])}
            onChange={(event) => field("provider", event.target.value)}
            placeholder="YouTube, Coursera, publisher…"
          />
          {errors["provider"] ? <small className="field-error">{errors["provider"]}</small> : null}
        </label>
        <label className="editor-field">
          <span>Resource type</span>
          <select
            data-material-field="resourceType"
            value={material.resourceType}
            aria-invalid={Boolean(errors["resourceType"])}
            onChange={(event) =>
              field("resourceType", event.target.value as LinkEditorMaterial["resourceType"])
            }
          >
            <option value="article">Article</option>
            <option value="video">Video</option>
            <option value="course">Course</option>
            <option value="training">Training</option>
          </select>
          {errors["resourceType"] ? (
            <small className="field-error">{errors["resourceType"]}</small>
          ) : null}
        </label>
        <label className="editor-field">
          <span>Duration in minutes</span>
          <input
            type="number"
            data-material-field="durationMinutes"
            min={1}
            value={material.durationMinutes ?? ""}
            aria-invalid={Boolean(errors["durationMinutes"])}
            onChange={(event) =>
              field("durationMinutes", event.target.value ? Number(event.target.value) : null)
            }
          />
          {errors["durationMinutes"] ? (
            <small className="field-error">{errors["durationMinutes"]}</small>
          ) : null}
        </label>
      </div>
    </div>
  );
}

function DuplicateLinkFeedback({
  materialId,
  state,
  onRetry,
  onSaveWithoutCheck,
}: {
  materialId: string;
  state: MaterialDuplicateState;
  onRetry: () => void;
  onSaveWithoutCheck: () => void;
}) {
  if (state.status === "idle") return null;
  if (state.status === "checking") {
    return (
      <div
        id={`${materialId}-duplicate-status`}
        className="duplicate-link-checking"
        role="status"
        aria-live="polite"
      >
        Checking…
      </div>
    );
  }
  if (state.status === "failed") {
    return (
      <div id={`${materialId}-duplicate-error`} className="duplicate-link-check-error" role="alert">
        <span>{state.message}</span>
        <span className="duplicate-link-check-actions">
          <button type="button" onClick={onRetry}>
            Retry check
          </button>
          <button type="button" onClick={onSaveWithoutCheck}>
            Save without checking
          </button>
        </span>
      </div>
    );
  }

  const headingId = `${materialId}-duplicate-heading`;
  return (
    <div
      id={`${materialId}-duplicate-warning`}
      className="duplicate-link-warning"
      role="alert"
      aria-labelledby={headingId}
      tabIndex={-1}
      data-duplicate-warning
    >
      <span className="duplicate-link-warning-icon" aria-hidden="true">
        <LinkIcon size={18} />
      </span>
      <div>
        <h3 id={headingId}>Already used in another activity</h3>
        {state.matches.length === 1 ? (
          <DuplicateActivityLine match={state.matches[0]!} />
        ) : (
          <ul>
            {state.matches.map((match) => (
              <li key={match.activityId}>
                <DuplicateActivityLine match={match} />
              </li>
            ))}
          </ul>
        )}
        <p className="duplicate-link-supporting">You can still save this activity.</p>
      </div>
    </div>
  );
}

function DuplicateActivityLine({ match }: { match: DuplicateActivityMatch }) {
  return (
    <p className="duplicate-link-match">
      You added this link to “{match.title}”.{" "}
      <a
        href={`/activities/${match.activityId}`}
        target="_blank"
        rel="noopener noreferrer"
        data-preserve-editor="true"
        aria-label={`View activity: ${match.title}`}
      >
        View activity <span aria-hidden="true">↗</span>
      </a>
    </p>
  );
}

function RichTextEditor({
  value,
  onChange,
  error,
}: {
  value: RichTextDocument;
  onChange: (value: RichTextDocument) => void;
  error: string | undefined;
}) {
  const editor = useEditor({
    injectCSS: false,
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        code: false,
        codeBlock: false,
        horizontalRule: false,
        strike: false,
        link: false,
      }),
      LinkExtension.configure({
        openOnClick: false,
        autolink: false,
        protocols: ["http", "https"],
      }),
    ],
    content: value as JSONContent,
    immediatelyRender: false,
    editorProps: {
      attributes: {
        "aria-label": "Rich text content",
        "aria-invalid": error ? "true" : "false",
        "data-material-field": "document",
        role: "textbox",
      },
    },
    onUpdate: ({ editor }) => {
      const parsed = richTextDocumentSchema.safeParse(editor.getJSON());
      if (parsed.success) onChange(parsed.data);
    },
  });
  const formatting = useEditorState({
    editor,
    selector: ({ editor: current }) => ({
      bold: current?.isActive("bold") ?? false,
      italic: current?.isActive("italic") ?? false,
      heading2: current?.isActive("heading", { level: 2 }) ?? false,
      heading3: current?.isActive("heading", { level: 3 }) ?? false,
      bulletedList: current?.isActive("bulletList") ?? false,
      numberedList: current?.isActive("orderedList") ?? false,
      link: current?.isActive("link") ?? false,
    }),
  });
  if (!editor) return <div className="rich-text-loading">Preparing editor…</div>;
  const usesMacShortcuts =
    typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/.test(navigator.platform);
  const heading2Shortcut = usesMacShortcuts ? "Command+Option+2" : "Ctrl+Alt+2";
  const heading3Shortcut = usesMacShortcuts ? "Command+Option+3" : "Ctrl+Alt+3";
  const preserveSelection = (event: ReactMouseEvent<HTMLButtonElement>) => event.preventDefault();
  const setLink = () => {
    const currentHref: unknown = editor.getAttributes("link")["href"];
    const href = window.prompt(
      "Link URL",
      typeof currentHref === "string" ? currentHref : "https://",
    );
    if (href === null) return;
    if (!href) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    const parsed = safeHttpUrlSchema.safeParse(href);
    if (parsed.success) editor.chain().focus().setLink({ href: parsed.data }).run();
  };
  return (
    <div className="rich-text-editor">
      <div className="rich-text-toolbar" role="toolbar" aria-label="Rich text formatting">
        <button
          type="button"
          aria-label="Bold"
          aria-pressed={formatting?.bold ?? false}
          onMouseDown={preserveSelection}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold size={15} />
        </button>
        <button
          type="button"
          aria-label="Italic"
          aria-pressed={formatting?.italic ?? false}
          onMouseDown={preserveSelection}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic size={15} />
        </button>
        <button
          type="button"
          aria-label="Heading 2"
          aria-keyshortcuts={usesMacShortcuts ? "Meta+Alt+2" : "Control+Alt+2"}
          aria-pressed={formatting?.heading2 ?? false}
          title={`Heading 2 (${heading2Shortcut})`}
          onMouseDown={preserveSelection}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        >
          <Heading2 size={15} />
        </button>
        <button
          type="button"
          aria-label="Heading 3"
          aria-keyshortcuts={usesMacShortcuts ? "Meta+Alt+3" : "Control+Alt+3"}
          aria-pressed={formatting?.heading3 ?? false}
          title={`Heading 3 (${heading3Shortcut})`}
          onMouseDown={preserveSelection}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        >
          <Heading3 size={15} />
        </button>
        <button
          type="button"
          aria-label="Bulleted list"
          aria-pressed={formatting?.bulletedList ?? false}
          onMouseDown={preserveSelection}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <List size={15} />
        </button>
        <button
          type="button"
          aria-label="Numbered list"
          aria-pressed={formatting?.numberedList ?? false}
          onMouseDown={preserveSelection}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered size={15} />
        </button>
        <button
          type="button"
          aria-label="Link"
          aria-pressed={formatting?.link ?? false}
          onMouseDown={preserveSelection}
          onClick={setLink}
        >
          <LinkIcon size={15} />
        </button>
      </div>
      <EditorContent editor={editor} />
      {error ? <small className="rich-text-error field-error">{error}</small> : null}
    </div>
  );
}

function UploadMaterialEditor({
  material,
  onUpdate,
  onFile,
  onRetry,
  errors,
}: {
  material: UploadEditorMaterial;
  onUpdate: (update: (material: EditorMaterial) => EditorMaterial) => void;
  onFile: (file: File) => void;
  onRetry: () => void;
  errors: MaterialFieldErrors;
}) {
  const selectFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) onFile(file);
    event.target.value = "";
  };
  return (
    <div className="upload-material">
      {material.kind === "image" && material.previewUrl ? (
        <img
          src={material.previewUrl}
          alt={material.decorative ? "" : material.altText || "Image preview"}
        />
      ) : null}
      <div className="upload-material-details">
        <label className="upload-picker">
          <Upload size={16} />
          <span>{material.file ? "Replace file" : `Choose ${material.kind}`}</span>
          <input
            type="file"
            data-material-field="fileId"
            aria-invalid={Boolean(errors["fileId"])}
            accept={
              material.kind === "image"
                ? "image/jpeg,image/png,image/webp,image/gif"
                : ".pdf,.txt,.md,.doc,.docx,.ppt,.pptx"
            }
            onChange={selectFile}
          />
        </label>
        {errors["fileId"] ? <small className="field-error">{errors["fileId"]}</small> : null}
        {material.file ? (
          <dl>
            <div>
              <dt>Name</dt>
              <dd>{material.file.originalFilename}</dd>
            </div>
            <div>
              <dt>Type</dt>
              <dd>{material.file.contentType}</dd>
            </div>
            <div>
              <dt>Size</dt>
              <dd>{formatBytes(material.file.size)}</dd>
            </div>
          </dl>
        ) : (
          <p>
            {material.kind === "image"
              ? "JPEG, PNG, WebP, or GIF up to 10 MB."
              : "PDF, text, Markdown, Word, or PowerPoint up to 25 MB."}
          </p>
        )}
        {material.uploadStatus === "uploading" ? (
          <div className="upload-progress" role="status">
            <span>Uploading {material.uploadProgress.toString()}%</span>
            <progress value={material.uploadProgress} max={100} />
          </div>
        ) : null}
        {material.uploadStatus === "failed" ? (
          <div className="material-operation-error" role="alert">
            {material.uploadError}
            <button type="button" onClick={onRetry}>
              Retry upload
            </button>
          </div>
        ) : null}
      </div>
      {material.kind === "image" ? (
        <div className="image-accessibility">
          <label className="editor-field">
            <span>Image description</span>
            <input
              data-material-field="altText"
              value={material.altText}
              disabled={material.decorative}
              aria-invalid={Boolean(errors["altText"])}
              onChange={(event) =>
                onUpdate((current) =>
                  isUploadMaterial(current) ? { ...current, altText: event.target.value } : current,
                )
              }
            />
            {errors["altText"] ? <small className="field-error">{errors["altText"]}</small> : null}
          </label>
          <label className="decorative-toggle">
            <input
              type="checkbox"
              checked={material.decorative}
              onChange={(event) =>
                onUpdate((current) =>
                  isUploadMaterial(current)
                    ? {
                        ...current,
                        decorative: event.target.checked,
                        altText: event.target.checked ? "" : current.altText,
                      }
                    : current,
                )
              }
            />
            This image is decorative
          </label>
        </div>
      ) : null}
    </div>
  );
}

function EditorLoading() {
  return (
    <AppShell mainClassName="activity-editor-screen">
      <div
        className="activity-editor-loading"
        aria-label="Loading activity editor"
        aria-busy="true"
      >
        Preparing your activity…
      </div>
    </AppShell>
  );
}

function EditorLoadError({ onRetry }: { onRetry: () => void }) {
  return (
    <AppShell mainClassName="activity-editor-screen">
      <div className="empty-state" role="alert">
        <h1>The activity editor could not be loaded</h1>
        <p>Your saved work remains safe.</p>
        <button type="button" className="soft-button" onClick={onRetry}>
          Try again
        </button>
      </div>
    </AppShell>
  );
}

function defaultFields(): EditorFields {
  return {
    title: "",
    summary: "",
    collectionId: null,
    estimatedMinutes: 30,
  };
}

function fieldsFromDocument(activity: ActivityDocument): EditorFields {
  return {
    title: activity.title,
    summary: activity.summary ?? "",
    collectionId: activity.collection?.id ?? null,
    estimatedMinutes: activity.estimatedMinutes,
  };
}

function normalizeFields(fields: EditorFields): EditorFields {
  return {
    title: fields.title,
    summary: fields.summary,
    collectionId: fields.collectionId || null,
    estimatedMinutes: fields.estimatedMinutes,
  };
}

function toEditorMaterial(material: ActivityMaterial | ActivityDraftMaterial): EditorMaterial {
  if (material.kind === "link") return { ...material, metadataStatus: "idle" };
  if (material.kind === "rich_text") return material;
  if ("uploadStatus" in material) {
    const interrupted = material.uploadStatus === "uploading";
    return {
      id: material.id,
      kind: material.kind,
      fileId: material.uploadStatus === "ready" ? material.fileId : null,
      file: material.file,
      altText: material.altText,
      decorative: material.decorative,
      uploadStatus: interrupted ? "failed" : material.uploadStatus,
      uploadProgress: material.uploadStatus === "ready" ? 100 : 0,
      uploadError: interrupted
        ? "The page closed before this upload finished. Choose the file to try again."
        : material.uploadError,
      previewUrl:
        material.kind === "image" && material.uploadStatus === "ready"
          ? (material.file?.contentUrl ?? null)
          : null,
    };
  }
  return {
    id: material.id,
    kind: material.kind,
    fileId: material.fileId,
    file: material.file,
    altText: material.kind === "image" ? material.altText : "",
    decorative: material.kind === "image" ? material.decorative : false,
    uploadStatus: "ready",
    uploadProgress: 100,
    uploadError: "",
    previewUrl: material.kind === "image" ? material.file.contentUrl : null,
  };
}

function materialInput(material: EditorMaterial): unknown {
  if (material.kind === "link") {
    return serializableLink(material);
  }
  if (material.kind === "rich_text") return material;
  if (!material.fileId || material.uploadStatus !== "ready") {
    return { id: material.id, kind: material.kind, fileId: null };
  }
  return material.kind === "image"
    ? {
        id: material.id,
        kind: material.kind,
        fileId: material.fileId,
        altText: material.altText,
        decorative: material.decorative,
      }
    : { id: material.id, kind: material.kind, fileId: material.fileId };
}

function serializableMaterials(materials: EditorMaterial[]): ActivityDraftMaterial[] {
  return materials.map((material): ActivityDraftMaterial => {
    if (material.kind === "link") {
      return serializableLink(material);
    }
    if (material.kind === "rich_text") return material;
    return {
      id: material.id,
      kind: material.kind,
      fileId: material.uploadStatus === "ready" ? material.fileId : null,
      file: material.file,
      altText: material.altText,
      decorative: material.decorative,
      uploadStatus: material.uploadStatus,
      uploadError:
        material.uploadError ||
        (material.uploadStatus === "uploading"
          ? "The upload was interrupted. Choose the file to try again."
          : ""),
    };
  });
}

function serializableLink(
  material: LinkEditorMaterial,
): Extract<ActivityMaterial, { kind: "link" }> {
  return {
    id: material.id,
    kind: "link",
    url: material.url,
    title: material.title,
    provider: material.provider,
    resourceType: material.resourceType,
    thumbnailUrl: material.thumbnailUrl,
    durationMinutes: material.durationMinutes,
  };
}

function linkWithRecognizedUrl(material: LinkEditorMaterial, url: string): LinkEditorMaterial {
  const youtubeVideoId = youtubeVideoIdFromUrl(url);
  return {
    ...material,
    url,
    metadataStatus: "idle",
    metadataError: "",
    ...(youtubeVideoId
      ? {
          provider: "YouTube",
          resourceType: "video" as const,
          thumbnailUrl: youtubeThumbnailUrl(youtubeVideoId),
        }
      : {}),
  };
}

function isUploadMaterial(material: EditorMaterial): material is UploadEditorMaterial {
  return material.kind === "image" || material.kind === "file";
}

async function cleanupNewUploads(
  materials: EditorMaterial[],
  initialFiles: Set<string>,
): Promise<void> {
  await Promise.allSettled(
    materials.flatMap((material) =>
      isUploadMaterial(material) && material.fileId && !initialFiles.has(material.fileId)
        ? [removeUpload(material.fileId)]
        : [],
    ),
  );
}

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    return error.problem?.detail ?? error.problem?.title ?? error.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

function formatBytes(bytes: number): string {
  if (bytes < 1_024) return `${bytes.toString()} B`;
  if (bytes < 1_024 * 1_024) return `${(bytes / 1_024).toFixed(1)} KB`;
  return `${(bytes / (1_024 * 1_024)).toFixed(1)} MB`;
}

function isActivityRouteState(value: unknown): value is ActivityRouteState {
  return typeof value === "object" && value !== null;
}

function formatScheduleActionDate(localDate: string): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${localDate}T00:00:00Z`));
}

function linksRequiringDuplicateCheck(
  write: Pick<ActivityWrite, "materials">,
  mode: "create" | "edit",
  initialUrls: Map<string, string>,
): DuplicateLinkCheckInput["links"] {
  return write.materials.flatMap((material) => {
    if (material.kind !== "link") return [];
    if (mode === "edit" && initialUrls.get(material.id)?.trim() === material.url.trim()) return [];
    return [{ materialId: material.id, url: material.url }];
  });
}

function editorLinksRequiringDuplicateCheck(
  materials: EditorMaterial[],
  mode: "create" | "edit",
  initialUrls: Map<string, string>,
): DuplicateLinkCheckInput["links"] {
  return materials.flatMap((material) => {
    if (material.kind !== "link") return [];
    const parsed = safeHttpUrlSchema.safeParse(material.url);
    if (!parsed.success) return [];
    if (mode === "edit" && initialUrls.get(material.id)?.trim() === parsed.data) return [];
    return [{ materialId: material.id, url: parsed.data }];
  });
}

function duplicateCheckFingerprint(links: DuplicateLinkCheckInput["links"]): string {
  return JSON.stringify(
    [...links]
      .sort((left, right) => left.materialId.localeCompare(right.materialId))
      .map(({ materialId, url }) => [materialId, url]),
  );
}

function duplicateStateForMaterial(
  state: DuplicateCheckState,
  materialId: string,
): MaterialDuplicateState {
  if (state.status === "checking" && state.materialIds.includes(materialId)) {
    return { status: "checking" };
  }
  if (state.status === "duplicate") {
    const matches = state.matchesByMaterial[materialId];
    return matches ? { status: "duplicate", matches } : { status: "idle" };
  }
  if (state.status === "failed" && state.materialIds.includes(materialId)) {
    return { status: "failed", message: state.message };
  }
  return { status: "idle" };
}

function urlDescriptionIds(
  materialId: string,
  error: string | undefined,
  duplicateState: MaterialDuplicateState,
): string | undefined {
  const ids = [
    ...(error ? [`${materialId}-url-error`] : []),
    ...(duplicateState.status === "checking" ? [`${materialId}-duplicate-status`] : []),
    ...(duplicateState.status === "duplicate" ? [`${materialId}-duplicate-warning`] : []),
    ...(duplicateState.status === "failed" ? [`${materialId}-duplicate-error`] : []),
  ];
  return ids.length > 0 ? ids.join(" ") : undefined;
}

function collectMaterialErrors(
  issues: ReadonlyArray<{ path: PropertyKey[]; message: string }>,
  materials: EditorMaterial[],
): Record<string, MaterialFieldErrors> {
  const errors: Record<string, MaterialFieldErrors> = {};
  for (const issue of issues) {
    if (issue.path[0] !== "materials") continue;
    const index = Number(issue.path[1]);
    const material = Number.isInteger(index) ? materials[index] : undefined;
    if (!material) continue;
    const field = materialIssueField(issue, material);
    errors[material.id] ??= {};
    errors[material.id]![field] ??= issue.message;
  }
  return errors;
}

function materialIssueField(issue: { path: PropertyKey[] }, material: EditorMaterial): string {
  return (
    issue.path[2]?.toString() ??
    (material.kind === "rich_text"
      ? "document"
      : material.kind === "image" || material.kind === "file"
        ? "fileId"
        : "url")
  );
}
